import React, { useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Dropdown, Tooltip } from 'antd';
import moment from 'moment';
import classNames from 'classnames';
import { statusToCopy } from '@helpers/awStatuses';
import { removeAssetVersionFromStackAction } from '@redux/actions/assetsAction';
import Loader from '@components/Loader';
import { AssetVersionItemDto } from '@api/Api';
import { useTypedSelector } from '@hooks';
import { assetPreviewThumbnails } from '@helpers/assetPreviewThumbnails';
import { ConfirmModal } from '@components/Modals';
import {
  toggleCompareVersionsModal,
  toggleEntityDeletionModal,
  toggleUploadAssetModal
} from '@redux/actions/modalAction';
import useTypedDispatch from '@hooks/useTypedDispatch';
import { useResponsive } from '@hooks/useResponsive';
import { mediaViewerPath } from '@helpers/uxpEmbeddedMediaViewer';
import useOnboardingProcess from '@hooks/useOnboardingProcess';
import './DropdownAssetVersions.less';
import { IS_UXP_WEBVIEW } from '@config/constants';
import {
  ArrowDownIcon,
  CheckIcon,
  CrossIcon,
  DeleteIcon,
  LockIcon,
  MediaViewerCompareIcon
} from '@icons';

type RenderVersionsMenuProps = {
  versions: AssetVersionItemDto[];
  isPrivate: boolean | null;
  selectedVersionId: string | number;
  external?: boolean;
  onChangeVersion(id: string): void;
  setIsDropdownVisible(v: boolean): void;
  isEmbeddedWebView?: boolean;
};

function RenderVersionsMenu({
  versions,
  isPrivate,
  selectedVersionId,
  external,
  onChangeVersion,
  setIsDropdownVisible,
  isEmbeddedWebView
}: RenderVersionsMenuProps) {
  const dispatch = useTypedDispatch();
  const navigate = useNavigate();
  const { isMdWidth } = useResponsive();
  const { tourData, goToStep } = useOnboardingProcess();
  const isAnnouncementMediaViewerTourMenu =
    tourData?.type === 'announcement-media-viewer' && goToStep === 3;
  const asset = useTypedSelector((state) => state.mediaViewer.assets);
  const [versionToDelete, setVersionToDelete] =
    useState<AssetVersionItemDto | null>(null);
  const preparePreview = (asset: AssetVersionItemDto) =>
    assetPreviewThumbnails({
      url: asset.metadata.preview?.url || '',
      status: asset.metadata.preview?.status || '',
      type: asset.metadata.type || ''
    });

  const isCompareVersionsDisabled =
    versions.length <= 1 && !asset?.asset?.permissions.uploadNewVersion;

  return (
    <div className="media_viewer_versions-menu">
      {versions.map((item, idx) => {
        const { id, name, createdAt, versionNumber } = item;
        const date = moment(new Date(createdAt)).format(
          moment(createdAt).minutes() === 59 ? 'MMM DD' : 'MMM DD, h:mma'
        );
        const isLastVersion = versions[0].id === item.id;
        const selected = id === selectedVersionId;
        return (
          <div
            key={id}
            className={`media_viewer_versions-item ${selected && 'b-selected'}`}
            onClick={() => {
              setIsDropdownVisible(false);
              onChangeVersion(id);
            }}
          >
            <div className="media_viewer_versions-image">
              {preparePreview(item) === 'pending' && (
                <Loader
                  classNameWrapper="assets-loader-wrapper"
                  spinSize={24}
                  isSpinning={true}
                />
              )}
              {preparePreview(item) !== 'pending' &&
              typeof preparePreview(item) === 'string' ? (
                <img
                  src={`${preparePreview(item)}`}
                  alt=""
                  className="assets_user_image"
                />
              ) : (
                preparePreview(item)
              )}
              {isPrivate && (
                <div className="media_viewer_versions-lock">
                  <LockIcon />
                </div>
              )}
            </div>
            <div className="media_viewer_versions-content">
              <div className="media_viewer_versions-top">
                <div className="media_viewer_versions-name">{name}</div>
                <div className="media_viewer_versions-label">
                  V{versionNumber}
                </div>
                <div />
                {!isEmbeddedWebView && (
                  <div
                    className={classNames('media_viewer_versions-action', {
                      'media_viewer_versions-selected': selected,
                      'media_viewer_versions-can-delete':
                        idx === 0 &&
                        (!external || (external && versions.length > 1))
                    })}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!asset) return;
                      if (versions.length > 1) {
                        setIsDropdownVisible(false);
                        setVersionToDelete(item);
                        return;
                      }

                      dispatch(
                        toggleEntityDeletionModal({
                          entity: asset,
                          page: 'asset',
                          permanent: false,
                          visible: true,
                          func: () => {
                            if (asset?.campaignId) {
                              navigate(`/campaigns/single/${asset.campaignId}`);
                              return;
                            }
                            if (asset?.workspaceId) {
                              navigate(`/campaigns/all/${asset?.workspaceId}`);
                              return;
                            }
                            navigate(`/`);
                          }
                        })
                      );
                    }}
                  >
                    <CheckIcon className="check-svg" />
                    {asset?.asset?.permissions.delete && (
                      <>
                        {versions.length > 1 ? (
                          <CrossIcon className="delete-svg" />
                        ) : (
                          <DeleteIcon className="delete-svg" />
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <div className="media_viewer_versions-date">{date}</div>
                {isLastVersion && asset?.asset?.approvalWorkflow?.status && (
                  <div className="media_viewer_versions-status">
                    {statusToCopy(asset?.asset?.approvalWorkflow)}
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })}
      {!isMdWidth && (
        <Tooltip
          title={
            isCompareVersionsDisabled
              ? 'Upload another version to compare'
              : undefined
          }
          overlayClassName="toggle_favorite_overlay"
          placement="bottom"
          trigger={isCompareVersionsDisabled ? undefined : []}
        >
          <div>
            <Button
              className={classNames(
                'media_viewer_compare-versions-btn',
                'tour-announcement-media-viewer-versions',
                {
                  'tour-announcement-media-viewer-versions-active':
                    isAnnouncementMediaViewerTourMenu
                }
              )}
              icon={<MediaViewerCompareIcon />}
              disabled={isCompareVersionsDisabled}
              onClick={() => {
                if (versions.length <= 1) {
                  if (!asset?.workspaceId) return;
                  dispatch(
                    toggleUploadAssetModal({
                      visible: true,
                      newVersionFromMediaViewer: true,
                      workspaceId: asset.workspaceId
                    })
                  );
                  setIsDropdownVisible(false);
                  return;
                }
                dispatch(
                  toggleCompareVersionsModal({
                    visible: true,
                    assetVersions: versions
                  })
                );
                setIsDropdownVisible(false);
              }}
            >
              Compare versions
            </Button>
          </div>
        </Tooltip>
      )}
      <ConfirmModal
        visible={!!versionToDelete}
        width={464}
        title="Delete media?"
        description={`Are you sure you want to delete current media version (v${versionToDelete?.versionNumber})?`}
        okText="Yes, delete media"
        onCancel={() => setVersionToDelete(null)}
        onConfirm={async () => {
          if (!asset || !versionToDelete) return;
          await dispatch(
            removeAssetVersionFromStackAction({
              assetId: asset.id,
              assetVersionId: versionToDelete.id
            })
          );
          setVersionToDelete(null);
        }}
      />
    </div>
  );
}

function DropdownAssetVersions({
  external,
  isEmbeddedWebView
}: {
  external?: boolean;
  isEmbeddedWebView?: boolean;
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isMdWidth } = useResponsive();
  const assets = useTypedSelector(({ mediaViewer }) => mediaViewer.assets);
  const selectedVersion = useTypedSelector(
    ({ mediaViewer }) => mediaViewer.selectedVersion
  );
  const { tourData, goToStep } = useOnboardingProcess();
  const isAnnouncementMediaViewerTourMenu =
    tourData?.type === 'announcement-media-viewer' && goToStep === 3;
  const isPrivate =
    !!assets?.isPrivate ||
    (!!assets?.path?.length && !assets?.path.some((x) => x.isPrivate));
  const [isDropdownVisible, setIsDropdownVisible] = useState<boolean>(false);
  const versions: AssetVersionItemDto[] = useMemo(
    () => assets?.asset?.versions || [],
    [assets]
  );

  const onChangeVersion = (id: string) => {
    if (external) {
      setSearchParams((prev) => {
        prev.set('versionId', id);
        return prev;
      });
    } else if (assets && selectedVersion?.id !== id) {
      const returnTo = location.state?.returnTo;
      const isEmbedded = IS_UXP_WEBVIEW;
      navigate(mediaViewerPath(assets.id, id, searchParams.toString()), {
        state: { returnTo },
        replace: isEmbedded
      });
    }
  };

  if (!Array.isArray(versions)) {
    return null;
  }

  return (
    <div className="media_viewer_versions">
      <Dropdown
        overlay={
          <RenderVersionsMenu
            versions={versions}
            external={external}
            isPrivate={isPrivate}
            onChangeVersion={onChangeVersion}
            selectedVersionId={selectedVersion?.id || ''}
            setIsDropdownVisible={setIsDropdownVisible}
            isEmbeddedWebView={isEmbeddedWebView}
          />
        }
        overlayClassName="media_viewer_versions-overlay"
        placement="bottom"
        visible={isDropdownVisible}
        onVisibleChange={(v) => setIsDropdownVisible(v)}
        trigger={['click']}
      >
        <Tooltip
          title="Media versions"
          overlayClassName="toggle_favorite_overlay"
          placement="bottom"
          trigger={isMdWidth ? [] : undefined}
        >
          <Button
            className={classNames(
              'media_viewer_btn tour-review-asset-versions',
              'j-tour-announcement-media-viewer-versions-btn',
              {
                'tour-announcement-media-viewer-versions-active':
                  isAnnouncementMediaViewerTourMenu
              }
            )}
          >
            V{selectedVersion?.versionNumber}
            <ArrowDownIcon />
          </Button>
        </Tooltip>
      </Dropdown>
    </div>
  );
}

export default DropdownAssetVersions;
